/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable @typescript-eslint/no-misused-promises */
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { DeviceManagementServiceRB } from '../deviceManagement/deviceManagement.service';
import { RBEvent, Webinar, WebinarSessionParticipant } from 'rainbow-web-sdk/models';
import { WebinarConferenceService } from './webinarConference.service';
import { Subject, Subscription, filter } from 'rxjs';
import { stringifyError } from '../../utils/stringifyError';

export enum WebinarAudioMixerEvents {
    STAGE_AUDIO_LEVEL_CHANGED = "STAGE_AUDIO_LEVEL_CHANGED",
    BACKSTAGE_AUDIO_LEVEL_CHANGED = "BACKSTAGE_AUDIO_LEVEL_CHANGED",
    AUDIO_BALANCE_CHANGED = "AUDIO_BALANCE_CHANGED"
}
export class WebinarAudioMixer {

    private mcuAudioElement?: HTMLAudioElement;
    private audioElements?: Map<WebinarSessionParticipant, HTMLAudioElement>;
    private sharingElements?: Map<WebinarSessionParticipant, HTMLAudioElement>;
    private logger: LoggerService = LoggerService.getInstance();
    private deviceManagement: DeviceManagementServiceRB = DeviceManagementServiceRB.getInstance();
    private audioMixerEvent: Subject<any> = new Subject<any>();
    private webinarExtendedBackstage: boolean = false;
    public backstageAudioLevel: number = 1;
    public stageAudioLevel: number = 1;
    public audioBalance: number = 0.8;

    constructor(
        private webinarConferenceService: WebinarConferenceService
    ) {}

    private initialize() {
        try {
            this.logger.info(`[WebinarService] AudioMixer.initialize ()`);
            this.audioElements = new Map<WebinarSessionParticipant, HTMLAudioElement>();
            this.sharingElements = new Map<WebinarSessionParticipant, HTMLAudioElement>();
            this.setAudioBalance(0.8);
        }
        catch (error) {
            this.logger.error(`[WebinarService] AudioMixer.initialize () - FAILURE - ${stringifyError(error)}`);
        }
    }

    public destroy(): void {
        this.audioElements.forEach((audioElement) => { audioElement.pause(); });
        this.sharingElements.forEach((audioElement) => { audioElement.pause(); });
        this.audioElements.clear();
        this.sharingElements.clear();
        this.logger.info(`[WebinarService] AudioMixer.destroy (audio: ${this.audioElements.size.toString()}, sharing: ${this.sharingElements.size.toString()})`);
    }

    public subscribe(handler: (event: RBEvent<WebinarAudioMixerEvents>) => any, eventNames?: string | string[]): Subscription {
        const observable = eventNames
            ? this.audioMixerEvent.pipe(filter((event: RBEvent) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.audioMixerEvent;

        return observable.subscribe((event: RBEvent<WebinarAudioMixerEvents>) => {
            try { handler(event); } catch { /**/ }
        });
    }

    public sendEvent(event: WebinarAudioMixerEvents): void {
        this.audioMixerEvent.next(RBEvent.create(event));
    }

    /* **************************************************** */
    /* **           BACKSTAGE AUDIO MANAGEMENT           ** */
    /* **************************************************** */
    public async addBackstageAudio(webinar: Webinar, webinarExtendeBackstage: boolean): Promise<void> {
        try {
            this.webinarExtendedBackstage = webinarExtendeBackstage;
            if (!this.audioElements) this.initialize();
            const audioOutputDevice = this.deviceManagement.getCurrentSpeaker();
            const stream = webinar.session.mcuAudioSession.remoteStreamsObject?.["0"];
            const sid = webinar.session.mcuAudioSession.sid;
            if (!stream || stream.getAudioTracks().length === 0) return;

            if (!this.mcuAudioElement) {
                this.mcuAudioElement = new Audio();
                this.logger.info(`[WebinarService] AudioMixer.addBackstageAudio (web: ${webinar.id},  part: ${webinar.session.localParticipant.id}, sid: ${sid}, device: ${audioOutputDevice?.label})`);
                if (!this.webinarExtendedBackstage) this.logger.info(`[WebinarService] AudioMixer.unmuteBackstageElem (web: ${webinar.id}, part: ${webinar.session.localParticipant.id}, sid: ${sid})`);
            }
            this.mcuAudioElement.srcObject = stream;
            await this.deviceManagement.setSpeakerForAudioElement(this.mcuAudioElement, audioOutputDevice);
            this.mcuAudioElement.volume = this.backstageAudioLevel;
            this.audioElemPlay(this.mcuAudioElement);
        }
        catch (error) {
            this.logger.error(`[WebinarService] AudioMixer.addBackstageAudio (web: ${webinar.id}) - FAILURE - ${stringifyError(error)}`);
        }
    }

    public muteBackstage(webinar: Webinar, muted: boolean = true): void {
        try {
            if (!this.audioElements) this.initialize();
            if (this.mcuAudioElement && this.mcuAudioElement.muted !== muted) {
                this.mcuAudioElement.muted = muted;
                this.logger.info(`[WebinarService] AudioMixer.${muted ? '' : 'un'}muteBackstageElem (web: ${webinar.id}, part: ${webinar.session.localParticipant.id})`);
            }
        }
        catch (error) {
            this.logger.error(`[WebinarService] AudioMixer.${muted ? '' : 'un'}muteBackstageElem (web: ${webinar.id}, part: ${webinar.session.localParticipant.id}) - FAILURE - ${stringifyError(error)}`);
        }
    }

    public unmuteBackstage(webinar: Webinar): void { this.muteBackstage(webinar, false); }

    public manageBackstageMic(webinar: Webinar, enabled: boolean): void {
        const localStream = webinar?.session?.localParticipant?.mcuAudioStream;
        const audioTrack = localStream?.getAudioTracks()[0];
        if (audioTrack && audioTrack.enabled !== enabled) {
            audioTrack.enabled = enabled;
            this.logger.info(`[WebinarService] AudioMixer.${enabled ? 'open' : 'close'}BackstageMic (web: ${webinar.id}, part: ${webinar.session.localParticipant.id})`);
        }
    }

    private createSilentAudioTrack(): MediaStreamTrack {
        const ctx = new AudioContext();
        const oscillator = ctx.createOscillator();
        const dst = ctx.createMediaStreamDestination();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(0.0001, ctx.currentTime);
        oscillator.connect(dst);
        oscillator.start();

        return dst.stream.getAudioTracks()[0];
    }

    /* **************************************************** */
    /* **           STAGE AUDIO MANAGEMENT               ** */
    /* **************************************************** */
    public async addAudioStream(webinar: Webinar, participant: WebinarSessionParticipant, type: 'audio' | 'sharing', videoType: string = ''): Promise<void> {
        const sid = type === 'sharing' ? participant?.sharingSession?.sid : participant?.audioVideoSession.sid;
        try {
            if (!this.audioElements) this.initialize();
            const stream = type === 'sharing' ? participant.sharingStream : participant.audioVideoStream;
            if (stream.getAudioTracks().length === 0) return;
            const audioOutputDevice = this.deviceManagement.getCurrentSpeaker();
            const audioElements = (type === 'audio' ? this.audioElements : this.sharingElements);
            let audioElement = audioElements.get(participant);
            if (!audioElement) {
                audioElement = new Audio();
                audioElements.set(participant, audioElement);
                this.logger.info(`[WebinarService] AudioMixer.addAudioStream (web: ${webinar.id}, type: ${type}${videoType}, part: ${participant.id}, audio: ${this.audioElements.size}, sharing: ${this.sharingElements.size}, sid: ${sid}, device: ${audioOutputDevice?.label})`);
            }
            else this.logger.info(`[WebinarService] AudioMixer.updateAudioStream (web: ${webinar.id}, type: ${type}${videoType}, part: ${participant.id}, audio: ${this.audioElements.size}, sharing: ${this.sharingElements.size}, sid: ${sid}, device: ${audioOutputDevice?.label})`);
            audioElement.srcObject = stream;
            await this.deviceManagement.setSpeakerForAudioElement(audioElement, audioOutputDevice);

            // handle audioLevel
            if (participant.onStage) audioElement.volume = this.stageAudioLevel;
            else audioElement.volume = this.backstageAudioLevel;

            this.audioElemPlay(audioElement);
        }
        catch (error) {
            this.logger.error(`[WebinarService] AudioMixer.addAudioStream (web: ${webinar.id}, type: ${type}, part: ${participant.id}) - FAILURE - ${stringifyError(error)}`);
        }
    }

    public async removeAudioStream(participant: WebinarSessionParticipant, type: 'audio' | 'sharing' | 'all'): Promise<void> {
        try {
            if (!this.audioElements) this.initialize();
            if (type === 'audio' || type === 'all') {
                const audioElem = this.audioElements.get(participant);
                if (audioElem) {
                    audioElem.pause();
                    this.audioElements.delete(participant);
                    this.logger.info(`[WebinarService] AudioMixer.removeAudioStream (type: audio, part: ${participant.id}, audio: ${this.audioElements.size}, sharing: ${this.sharingElements.size})`);
                }
            }
            if (type === 'sharing' || type === 'all') {
                const sharingElement = this.sharingElements.get(participant);
                if (sharingElement) {
                    sharingElement.pause();
                    this.sharingElements.delete(participant);
                    this.logger.info(`[WebinarService] AudioMixer.removeAudioStream (type: sharing, part: ${participant.id}, audio: ${this.audioElements.size}, sharing: ${this.sharingElements.size})`);
                }
            }
        }
        catch (error) {
            this.logger.error(`[WebinarService] AudioMixer.removeAudioStream (type: ${type}, part: ${participant.id}) - FAILURE - ${error}`);
        }
    }

    public mute(participant: WebinarSessionParticipant, type: 'audio' | 'sharing' | 'all', muted: boolean = true): void {
        const webinar = this.webinarConferenceService.activeWebinar;
        if (!webinar) return;
        try {
            if (!this.audioElements) this.initialize();
            if (type === 'audio' || type === 'all') {
                const audioElem = this.audioElements.get(participant);
                if (audioElem && audioElem.muted !== muted) {
                    audioElem.muted = muted;
                    participant.audible = !muted;
                    this.logger.info(`[WebinarService] AudioMixer.${muted === true ? '' : 'un'}muteAudioElem (web: ${webinar.id}, part: ${participant.id}, type: ${type})`);
                }
            }
            if (type === 'sharing' || type === 'all') {
                const sharingElement = this.sharingElements.get(participant);
                if (sharingElement && sharingElement.muted !== muted) {
                    sharingElement.muted = muted;
                    this.logger.info(`[WebinarService] AudioMixer.${muted === true ? '' : 'un'}muteAudioElem (web: ${webinar.id}, part: ${participant.id}, type: ${type})`);
                }
            }
        }
        catch (error) {
            this.logger.error(`[WebinarService] AudioMixer.${muted === true ? '' : 'un'}muteAudioElem (web: ${webinar.id}, part: ${participant.id}, type: ${type}) - FAILURE - ${error}`);
        }
    }

    public unmute(participant: WebinarSessionParticipant, type: 'audio' | 'sharing' | 'all'): void {
        this.mute(participant, type, false);
    }

    public activateAudio(): void {
        if (!this.audioElements) this.initialize();
        this.logger.info(`[WebinarService] AudioMixer.activateAudio ()`);
        this.audioElements?.forEach((audioElement) => { this.audioElemPlay(audioElement); });
        this.sharingElements?.forEach((audioElement) => { this.audioElemPlay(audioElement); });
    }

    public async updateAudioDevice(): Promise<void> {
        const audioOutputDevice = this.deviceManagement.getCurrentSpeaker();
        this.logger.info(`[WebinarService] AudioMixer.updateAudioDevice (${audioOutputDevice?.label})`);
        if (!audioOutputDevice) return;

        this.audioElements.forEach(async (audioElement) => {
            await this.deviceManagement.setSpeakerForAudioElement(audioElement, audioOutputDevice);
            this.audioElemPlay(audioElement);
        });

        if (this.mcuAudioElement) {
            await this.deviceManagement.setSpeakerForAudioElement(this.mcuAudioElement, audioOutputDevice);
            this.audioElemPlay(this.mcuAudioElement);
        }

        this.sharingElements.forEach(async (audioElement) => {
            await this.deviceManagement.setSpeakerForAudioElement(audioElement, audioOutputDevice);
            this.audioElemPlay(audioElement);
        });
    }

    private audioElemPlay(audioElement: HTMLAudioElement): void {
        audioElement?.play().catch(() => {
            if (!this.webinarConferenceService.isInteractEventSent) {
                this.webinarConferenceService.isInteractEventSent = true;
                this.webinarConferenceService.activeWebinarSubject.next(RBEvent.create('ON_ACTIVE_WEBINAR_NOSOUND', { webinar: this.webinarConferenceService.activeWebinar }));
            }
        });
    }

    public updateAudioLevelForParticipant(participant: WebinarSessionParticipant): void {
        if (participant) {
            if (participant.onStage) { this.setAudioLevel(participant, this.stageAudioLevel); }
            else { this.setAudioLevel(participant, this.backstageAudioLevel); }
        }
    }

    public setAudioBalance(volume: number): void {
        this.audioBalance = volume;
        const volumeStage = Math.cos(volume * 0.5 * Math.PI);
        const volumeBackstage = Math.sin(volume * 0.5 * Math.PI);
        this.setStageAudioLevel(volumeStage);
        this.setBackstageAudioLevel(volumeBackstage);
        this.sendEvent(WebinarAudioMixerEvents.AUDIO_BALANCE_CHANGED);

    }

    private setStageAudioLevel(audioLevel: number): void {
        if (!this.webinarConferenceService.activeWebinar || !this.webinarConferenceService.activeWebinar.session) {
            this.logger.debug(`[WebinarService] AudioMixer.setStageAudioLevel () -- activeWebinar or session is undefined`);
            return;
        }

        if (audioLevel < 0 || audioLevel > 1) {
            this.logger.debug(`[WebinarService] AudioMixer.setStageAudioLevel () -- audio levels must be between 0 and 1`);
            return;
        }

        const webinar = this.webinarConferenceService.activeWebinar;
        this.logger.info(`[WebinarService] AudioMixer.setStageAudioLevel (web: ${webinar.id}, part: ${webinar.session.localParticipant.id}, audioLevel: ${audioLevel})`);
        this.stageAudioLevel = audioLevel;
        const stageParticipants = webinar.session.speakerParticipants.filter(participant => participant.onStage);
        stageParticipants.forEach((participant) => { this.setAudioLevel(participant, audioLevel); });
        this.sendEvent(WebinarAudioMixerEvents.STAGE_AUDIO_LEVEL_CHANGED);
    }

    private setBackstageAudioLevel(audioLevel: number): void {
        if (!this.webinarConferenceService.activeWebinar || !this.webinarConferenceService.activeWebinar.session) {
            this.logger.debug(`[WebinarService] AudioMixer.setBackstageAudioLevel () - activeWebinar or session is undefined`);
            return;
        }

        if (audioLevel < 0 || audioLevel > 1) {
            this.logger.debug(`[WebinarService] AudioMixer.setBackstageAudioLevel () - audio levels must be between 0 and 1`);
            return;
        }

        const webinar = this.webinarConferenceService.activeWebinar;
        if (this.webinarExtendedBackstage) {
            if (webinar?.session?.localParticipant?.onStage) {
                this.backstageAudioLevel = audioLevel;
                const backstageParticipants = webinar.session.speakerParticipants.filter(participant => !participant.onStage);
                backstageParticipants.forEach((participant) => { this.setAudioLevel(participant, audioLevel); });
            }
            else if (this.mcuAudioElement) {
                this.backstageAudioLevel = audioLevel;
                this.mcuAudioElement.volume = audioLevel;
            }
        }
        else {
            this.backstageAudioLevel = audioLevel;
            const backstageParticipants = webinar.session.speakerParticipants.filter(participant => !participant.onStage);
            backstageParticipants.forEach((participant) => { this.setAudioLevel(participant, audioLevel); });
        }
        this.sendEvent(WebinarAudioMixerEvents.BACKSTAGE_AUDIO_LEVEL_CHANGED);
    }

    private setAudioLevel(participant: WebinarSessionParticipant, level: number): void {
        if (this.audioElements) {
            const audioElem = this.audioElements.get(participant);
            if (level < 0) level = 0;
            if (level > 1) level = 1;
            if (audioElem) audioElem.volume = level;
        }
    }
}
